import { deserializeBuilderAsset, SerializedBuilderAsset, BuilderAsset } from "../../src/entities/BuilderAsset";
import { AssetCategory } from "../../src/entities/AssetCategory";
import { AspectRatios } from "../entities/AspectRatios";
import { FormFactor, OperatingSystem } from "../utilities/UserAgent";
import { WebClient } from "./server/WebClient";


export interface SaveBuilderAssetRequest {
	id?: string;
	parentId?: string;
	name: string;
	isStarred: boolean;
	dateAccessed: Date;
	category: AssetCategory;
	text: string;
	slideCount: number;
	isTemplate: boolean;
	templateDescription: string;
	slideExporterAspectRatio?: AspectRatios;
	customSlideBackgroundImagePath?: string;
	offlineMode: boolean;
	lastModifiedOnOperatingSystem?: OperatingSystem;
	lastModifiedFormFactor?: FormFactor;
}

export interface SaveCustomSlideBackgroundImageRequest {
	fileContents: string;
	id: string;
}

export interface SetOfflineModeRequest {
	builderAssetId: string;
	offlineMode: boolean;
}


export const BuilderAssetService = {
	get: (id: string) => WebClient.Get(`api/BuilderAssets/GetBuilderAsset/${id}`, deserializeBuilderAsset),

	getSermonAsBuilderAsset: (sermonId:string)=> WebClient.Get(`api/BuilderAssets/GetSermonAsBuilderAsset/${sermonId}`, deserializeBuilderAsset),

	downloadAsPdf: async (builderAsset: BuilderAsset) => {
		const response = await fetch(`/api/BuilderAssets/pdf/${builderAsset.id}`)
		const blob = await response.blob();
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.target = "_blank"
		link.href = url;
		link.download = `${builderAsset.name}.pdf`
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	},

	downloadAsDocx: async (builderAsset: BuilderAsset) => {
		const response = await fetch(`/api/BuilderAssets/docx/${builderAsset.id}`)
		const blob = await response.blob();
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.target = "_blank"
		link.href = url;
		link.download = `${builderAsset.name}.docx`
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	},

	getOfflineBuilderAssets: () => 
		WebClient.Get(`api/BuilderAssets/GetOfflineBuilderAssets`, (response: SerializedBuilderAsset[]) => response.map(deserializeBuilderAsset)),

	save: (builderAssetToSave: SaveBuilderAssetRequest) => 
		WebClient.Post.Validated(`api/BuilderAssets/SaveBuilderAsset/`, builderAssetToSave, deserializeBuilderAsset),

	saveFromTemplate: (builderAssetToSave: SaveBuilderAssetRequest) => 
		WebClient.Post.Validated(`api/BuilderAssets/SaveFromTemplateAsset/`, builderAssetToSave, deserializeBuilderAsset),

	saveTemplate: (builderAssetToSave: SaveBuilderAssetRequest) => 
		WebClient.Post.Validated(`api/BuilderAssets/SaveTemplateAsset/`, builderAssetToSave, deserializeBuilderAsset),

	getTemplates: () => 
		WebClient.Get("api/BuilderAssets/GetTemplates", (response: SerializedBuilderAsset[]) => response.map(deserializeBuilderAsset)),

	remove: (id: string) => WebClient.Delete(`api/BuilderAssets/DeleteBuilderAsset/${id}`),

	undeleteBuilderAsset: (id: string) => 
		WebClient.Put.Validated(`api/BuilderAssets/UndeleteBuilderAsset/`, { id }, deserializeBuilderAsset),

	setName: (id: string, name: string) => 
		WebClient.Put.Validated(`api/BuilderAssets/SetNameBuilderAsset`, { id, name }, deserializeBuilderAsset),

	setStarred: (id: string, isStarred: boolean) => 
		WebClient.Put.Validated(`api/BuilderAssets/SetStarredBuilderAsset`, { id, isStarred }, deserializeBuilderAsset),

	move: (id: string, parentId: string | null) => 
		WebClient.Put.Validated(`api/BuilderAssets/MoveBuilderAsset`, { id, parentId }, deserializeBuilderAsset),

	accessAsset: (id: string) => 
		WebClient.Put.Validated(`api/BuilderAssets/WasAccessed/${id}`, {}, deserializeBuilderAsset),

	removeCustomSlideBackgroundImageUrl: (id: string) => 
		WebClient.Delete(`api/BuilderAssets/DeleteSlideExporterCustomBackgroundImage/${id}`),

	generatePowerPointUploadUrl: (id: string, fileName: string) => 
		WebClient.Get<{ url: string }>(`api/BuilderAssets/GenerateCustomSlideImageUploadUrl?id=${id}&fileName=${fileName}`),

	uploadSourceImage: (uploadUrl: string, file: File, progress?: (percentage: number) => void) => 
		WebClient.Put.File.External(uploadUrl, file, progress),

	saveSlideExporterCustomBackgroundImage: (request: SaveCustomSlideBackgroundImageRequest) => 
		WebClient.Put.Validated("api/BuilderAssets/SaveSlideExporterCustomBackgroundImage/", request, deserializeBuilderAsset),

	getCustomBackgroundImageForDownload: (id: string) => 
		WebClient.Get<{ url: string }>(`api/BuilderAssets/GetCustomSlideBackgroundDownload/${id}`),

	saveSlideAspectRatio: (id: string, aspectRatio: AspectRatios) => 
		WebClient.Put.Validated(`api/BuilderAssets/SaveSlideAspectRatio/${id}/${aspectRatio}`, {}, deserializeBuilderAsset),

	getPublicUrlForCustomSlideImage: (id: string) => 
		WebClient.Get<{ customSlideBackgroundImagePath: string }>(`api/BuilderAssets/GetCdnUrlForCustomSlideBackground/${id}`),

	setOfflineMode: (id: string, offlineMode: boolean) => {
		const request: SetOfflineModeRequest = { builderAssetId: id, offlineMode };
		return WebClient.Put.Validated(`api/BuilderAssets/SetOfflineMode`, request, deserializeBuilderAsset);
	},

	getCustomBackgroundImageAsArrayBuffer: async (id: string) => {
		return await fetch(`api/BuilderAssets/image/${id}`)
			.then((res) => res.body)
			.then((body) => {
				if (body === null) return;

				const reader = body.getReader();

				return new ReadableStream({
					async start(controller) {
						while (true) {
							const { done, value } = await reader.read();

							if (done) break;

							controller.enqueue(value);
						}

						controller.close();
						reader.releaseLock();
					},
				});
			})
			.then((res) => new Response(res))
			.then((res) => res.blob())
			.then((blob) => blob.arrayBuffer());
	}
}
